/**
 * Le devis : ce qu'il doit porter, et ce que sa rédaction engage.
 *
 * LA FACTURATION ÉLECTRONIQUE NE LE CONCERNE PAS, et c'est la première chose
 * que le parcours dit. La réforme porte sur les FACTURES : les recevoir sous
 * forme structurée depuis le 1er septembre 2026, les émettre par une plateforme
 * habilitée à partir du 1er septembre 2027 pour une micro-entreprise, et
 * l'e-reporting qui suit ce calendrier. Un devis n'est pas une facture : c'est
 * une offre de contrat, antérieure à la vente, qui ne constate rien, n'entre
 * dans aucune séquence de numérotation et ne crée aucune créance. Il continue
 * de circuler en PDF, par courriel ou sur papier, sans plateforme.
 *
 * Ce qui change, en revanche, tient en une ligne : la facture qui suivra devra
 * porter le SIREN du client professionnel. Le devis est le moment où on le
 * demande sans effort, pendant que le client est motivé. Le parcours le
 * récupère donc dès maintenant.
 *
 * TROIS PIÈGES QUE LE DOCUMENT DOIT DÉSAMORCER, et qui coûtent tous de
 * l'argent :
 *
 *   1. UNE TVA ÉCRITE PAR ERREUR EST DUE du seul fait d'être écrite, sans que
 *      le client puisse la déduire. En franchise, il n'y a aucune ligne de
 *      taxe, seulement une mention. Le module refuse de composer un devis en
 *      franchise qui porterait un taux.
 *   2. LE MOT EMPLOYÉ POUR LA SOMME VERSÉE À LA COMMANDE décide de ce que vaut
 *      la signature. Face à un particulier, un devis muet sur ce point fait de
 *      cette somme des ARRHES : le client se dédit en l'abandonnant, et le
 *      professionnel qui se dédit en rend le double. Écrire « acompte » rend la
 *      commande ferme des deux côtés. Le document écrit donc le mot, toujours.
 *   3. UN DEVIS SIGNÉ NE FAIT ENTRER AUCUN EURO. Le chiffre d'affaires se
 *      compte à l'encaissement, jamais à la signature.
 *
 * LA MENTION DE FRANCHISE SUIT LA DATE DU DEVIS. Les règles de TVA changent de
 * code le 1er janvier 2027 : un devis daté du 31 décembre 2026 porte « TVA non
 * applicable, article 293 B du CGI », celui du lendemain « TVA non applicable,
 * article L. 233-3 du CIBS ». La règle, ses sources et ses pièges vivent dans
 * vatMentions.js, que la facturation à l'étranger lit aussi. Un devis établi
 * en 2026 et valable au-delà du 1er janvier le dit à son auteur : la facture
 * qui le suivra portera la nouvelle référence.
 */

import { dateEnToutesLettres, identite, nettoyer } from "./documentText.js";
import { BASCULE_CIBS, jourDu, mentionFranchise } from "./vatMentions.js";

/**
 * Les catégories d'activité où un devis écrit est imposé par la réglementation.
 *
 * La liste détaillée, activité par activité, est tenue à jour par Service
 * Public Entreprendre : elle descend à des seuils de montant que le référentiel
 * n'arbitre pas, et qui ne se chiffrent donc pas ici. Ces catégories suffisent
 * à dire au lecteur qu'il doit aller la lire.
 */
export const ACTIVITES_DEVIS_OBLIGATOIRE = [
    "batiment",
    "demenagement",
    "locationVoiture",
    "optique",
    "audioprothese",
    "servicesALaPersonne",
    "chirurgieEsthetique",
    "funeraire",
];

/** Le calendrier de la réforme, pour ce que le devis doit anticiper. */
export const EMISSION_ELECTRONIQUE_MICRO = "2027-09-01";

/** Une date ISO découpée, ou null si elle n'en est pas une. */
function jour(iso) {
    const t = nettoyer(iso);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [a, m, j] = t.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
    return { a, m, j, date: d };
}

/** Le nombre de jours entre deux dates ISO, négatif si la seconde précède. */
export function joursEntre(debut, fin) {
    const a = jour(debut);
    const b = jour(fin);
    if (!a || !b) return null;
    return Math.round((b.date - a.date) / 86400000);
}

/**
 * Un montant en euros, accepté avec virgule ou point.
 *
 * Le prix unitaire d'une ligne peut descendre au centime ; c'est le seul
 * endroit où deux décimales servent vraiment.
 */
export function montant(brut) {
    const t = nettoyer(String(brut ?? ""))
        .replace(/\s/g, "")
        .replace(",", ".");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,9}(\.\d{1,2})?$/.test(t)) return { valeur: 0, motif: "format" };
    const n = Number(t);
    if (n <= 0) return { valeur: n, motif: "nul" };
    return { valeur: Math.round(n * 100) / 100, motif: null };
}

/**
 * Une quantité : des heures, des mètres, des pièces.
 *
 * Trois décimales, parce qu'un quart d'heure vaut 0,25 et qu'un métreur écrit
 * 12,375 m². Le zéro est refusé : une ligne à zéro n'a rien à faire sur un
 * devis, elle en gonfle la lecture sans rien facturer.
 */
export function quantite(brut) {
    const t = nettoyer(String(brut ?? ""))
        .replace(/\s/g, "")
        .replace(",", ".");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,6}(\.\d{1,3})?$/.test(t)) return { valeur: 0, motif: "format" };
    const n = Number(t);
    if (n <= 0) return { valeur: n, motif: "nul" };
    return { valeur: Math.round(n * 1000) / 1000, motif: null };
}

/** Le SIRET de l'émetteur : quatorze chiffres. */
export function siret(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{14}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/** Le SIREN du client professionnel : neuf chiffres. */
export function siren(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{9}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/**
 * Un montant écrit comme un document l'écrit.
 *
 * Le séparateur de milliers rendu par `toLocaleString` en français est une
 * ESPACE FINE INSÉCABLE (U+202F), absente de l'encodage WinAnsi des polices
 * standard d'un PDF : elle en sortirait comme un caractère de substitution au
 * milieu du prix. Elle est ramenée à l'espace insécable ordinaire.
 */
export function euros(valeur) {
    const chiffres = valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${chiffres.replace(/ /g, " ")} €`;
}

/**
 * Un montant écrit AU MILIEU D'UNE PHRASE, en toutes lettres pour l'unité.
 *
 * Le symbole € n'a AUCUNE LARGEUR D'AVANCE dans les polices standard d'un PDF :
 * jsPDF n'émet pas de tableau `/Widths`, le lecteur retombe sur les métriques
 * Helvetica d'Adobe, et celles-ci ne connaissent pas le glyphe « Euro ». Le
 * caractère s'affiche, mais le mot suivant se pose par-dessus : « 1 617,00 €est
 * exigible ». Le défaut reste invisible tant que le symbole finit la ligne,
 * ce qui était le cas partout jusqu'au devis.
 *
 * En fin de cellule ou de ligne, `euros` garde donc le symbole. Dans une phrase
 * qui continue, c'est ce libellé-ci qui s'emploie, et il se lit mieux.
 */
export function eurosEnPhrase(valeur) {
    const chiffres = valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${chiffres.replace(/\u202f/g, "\u00a0")} euros`;
}

/** Une quantité écrite sans décimales inutiles : 3 et non 3,000. */
export function nombre(valeur) {
    return valeur
        .toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })
        .replace(/ /g, " ");
}

/** Un taux écrit à la française, virgule comprise. */
export function pourcent(taux) {
    return `${String(taux).replace(".", ",")} %`;
}

/**
 * Une adresse en plusieurs lignes, telle qu'elle a été saisie.
 *
 * `nettoyer` écrase les retours à la ligne avec le reste des blancs : le
 * découpage passe donc avant le nettoyage, ligne par ligne.
 */
export function lignesDAdresse(brut) {
    return String(brut ?? "")
        .split(/\r?\n/)
        .map((ligne) => nettoyer(ligne))
        .filter(Boolean);
}

/**
 * Les lignes du devis, chiffrées.
 *
 * Une ligne sans désignation est ignorée : le parcours en propose toujours une
 * vide en dernière position, et elle ne doit pas se retrouver dans le document
 * ni faire échouer la validation.
 */
export function lignesChiffrees(brutes) {
    const gardees = [];
    for (const ligne of brutes || []) {
        const designation = nettoyer(ligne?.designation);
        const q = quantite(ligne?.quantite);
        const p = montant(ligne?.prixUnitaire);
        // Une ligne entièrement vide n'existe pas : elle attend d'être remplie.
        if (!designation && q.motif === "vide" && p.motif === "vide") continue;
        gardees.push({
            designation,
            quantite: q.valeur,
            prixUnitaire: p.valeur,
            montant: Math.round(q.valeur * p.valeur * 100) / 100,
            motifs: {
                designation: designation ? null : "vide",
                quantite: q.motif,
                prixUnitaire: p.motif,
            },
        });
    }
    return gardees;
}

/** Le total du devis, et ce qui reste à régler après l'acompte. */
export function totaux(reponses) {
    const lignes = lignesChiffrees(reponses?.lignes);
    const complet = lignes.every((l) => !l.motifs.designation && !l.motifs.quantite && !l.motifs.prixUnitaire);
    if (!lignes.length || !complet) return null;

    const total = Math.round(lignes.reduce((somme, l) => somme + l.montant, 0) * 100) / 100;
    const part = Number(String(reponses?.acomptePourcent ?? "").replace(",", "."));
    const avecAcompte = reponses?.acompte === "oui" && Number.isFinite(part) && part > 0 && part <= 100;
    const acompte = avecAcompte ? Math.round(total * part) / 100 : 0;

    return {
        lignes,
        total,
        acompte,
        acomptePourcent: avecAcompte ? Math.round(part * 100) / 100 : 0,
        solde: Math.round((total - acompte) * 100) / 100,
    };
}

/**
 * Le devis est-il imposé, ou seulement conseillé ?
 *
 * Entre professionnels, aucune règle générale ne l'impose. Face à un
 * particulier, le code de la consommation impose d'informer sur les
 * caractéristiques essentielles, le prix et le délai AVANT la conclusion du
 * contrat, et le devis reste la façon la plus simple de prouver que cette
 * information a été donnée. Il devient franchement obligatoire dans une liste
 * d'activités fixée par la réglementation.
 */
export function obligation(reponses) {
    const r = reponses || {};
    if (!r.client) return { niveau: null };
    if (r.activiteReglementee && r.activiteReglementee !== "aucune") {
        return { niveau: "impose", activite: r.activiteReglementee };
    }
    if (r.client === "particulier") return { niveau: "preuve" };
    return { niveau: "usage" };
}

/**
 * Le régime de TVA du devis, et la mention qui l'accompagne.
 *
 * Une TVA affichée par erreur se retrouve sur la facture, où elle devient due
 * du seul fait d'y figurer, sans que le client puisse la déduire. Le devis en
 * franchise ne porte donc AUCUN taux, seulement la mention.
 */
export function regimeDeTva(reponses) {
    const r = reponses || {};
    if (r.tva !== "redevable") {
        // La mention suit la date du devis. Sans date lisible, elle reste vide,
        // et le devis refuse de toute façon de se composer.
        return { franchise: true, mention: mentionFranchise(r.dateDevis), taux: null };
    }
    const taux = Number(String(r.tauxTva ?? "").replace(",", "."));
    if (!Number.isFinite(taux) || taux <= 0 || taux > 100) {
        return { franchise: false, mention: null, taux: null };
    }
    return { franchise: false, mention: null, taux: Math.round(taux * 100) / 100 };
}

/**
 * Ce que vaut la somme demandée à la commande.
 *
 * C'est la règle la plus chère du sujet, et elle tient à un mot. Sans
 * qualification écrite, la somme versée par un particulier est réputée être des
 * ARRHES : il peut renoncer en les abandonnant, et si c'est le professionnel
 * qui renonce, il en rend le double. Le document écrit donc « acompte » en
 * toutes lettres dès qu'un versement est demandé, et le parcours dit pourquoi.
 */
export function natureDuVersement(reponses) {
    const r = reponses || {};
    if (r.acompte !== "oui") return { versement: false, nature: null };
    return {
        versement: true,
        nature: "acompte",
        // Le risque n'existe que face à un particulier : entre professionnels,
        // la présomption d'arrhes de l'article 1590 du code civil ne joue pas.
        risqueSiMuet: r.client === "particulier",
    };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    const requis = [
        ["client", r.client],
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["adresse", r.adresse],
        ["numero", r.numero],
        ["objet", r.objet],
        ["nomClient", r.nomClient],
        ["adresseClient", r.adresseClient],
        ["lieuExecution", r.lieuExecution],
        ["modalitesDePaiement", r.modalitesDePaiement],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const identifiant = siret(r.siret);
    if (identifiant.motif === "vide") manques.push("siret");
    else if (identifiant.motif) invalides.push({ champ: "siret", motif: identifiant.motif });

    // Le SIREN du client n'est demandé qu'à un professionnel, et il n'est pas
    // exigé aujourd'hui : son absence est une alerte, jamais un refus.
    if (r.client === "professionnel") {
        const identifiantClient = siren(r.sirenClient);
        if (identifiantClient.motif === "vide") alertes.push("sirenClientAbsent");
        else if (identifiantClient.motif) {
            invalides.push({ champ: "sirenClient", motif: identifiantClient.motif });
        }
    }

    for (const cle of ["dateDevis", "validiteJusquAu", "lieuDeSignature"]) {
        if (cle === "lieuDeSignature") continue;
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!jour(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    // Une offre ne peut pas expirer avant d'être faite.
    const duree = joursEntre(r.dateDevis, r.validiteJusquAu);
    if (duree !== null && duree < 0) {
        invalides.push({ champ: "validiteJusquAu", motif: "avantLeDevis" });
    }
    // Une offre déjà expirée le jour où on la remet ne vaut rien.
    if (duree !== null && duree >= 0) {
        const restant = joursEntre(String(aujourdhui).slice(0, 10), r.validiteJusquAu);
        if (restant !== null && restant < 0) alertes.push("validiteDepassee");
    }

    const lignes = lignesChiffrees(r.lignes);
    if (!lignes.length) manques.push("lignes");
    for (const [rang, ligne] of lignes.entries()) {
        for (const [champ, motif] of Object.entries(ligne.motifs)) {
            if (motif) invalides.push({ champ: `ligne${rang}.${champ}`, motif, rang });
        }
    }

    const tva = regimeDeTva(r);
    if (r.tva === "redevable" && tva.taux === null) {
        if (!nettoyer(r.tauxTva)) manques.push("tauxTva");
        else invalides.push({ champ: "tauxTva", motif: "format" });
    }

    if (r.acompte === "oui") {
        const part = Number(String(r.acomptePourcent ?? "").replace(",", "."));
        if (!nettoyer(r.acomptePourcent)) manques.push("acomptePourcent");
        else if (!Number.isFinite(part) || part <= 0 || part > 100) {
            invalides.push({ champ: "acomptePourcent", motif: "format" });
        }
    }

    if (r.devisPayant === "oui") {
        const prix = montant(r.prixDuDevis);
        if (prix.motif === "vide") manques.push("prixDuDevis");
        else if (prix.motif) invalides.push({ champ: "prixDuDevis", motif: prix.motif });
    }

    // Un devis établi avant le 1er janvier 2027 et valable au-delà porte la
    // mention de sa date, mais la facture qui le suivra portera celle du CIBS :
    // le dire pendant que le devis s'écrit, pas le jour de la facture.
    if (tva.franchise) {
        const etabli = jourDu(r.dateDevis);
        const valable = jourDu(r.validiteJusquAu);
        if (etabli && valable && etabli < BASCULE_CIBS && valable >= BASCULE_CIBS) {
            alertes.push("mentionBascule");
        }
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * Le devis lui-même.
 *
 * L'ordre suit celui que le code de la consommation attend d'une information
 * précontractuelle : qui, à qui, quoi, combien, jusqu'à quand, et à quelles
 * conditions. Le pavé d'acceptation ferme le document, parce que c'est ce que
 * le client doit avoir sous les yeux au moment de signer.
 */
export function composerDevis(reponses, aujourdhui) {
    const r = reponses || {};
    if (!valider(r, aujourdhui).valide) return null;

    const compte = totaux(r);
    if (!compte) return null;

    const tva = regimeDeTva(r);
    const versement = natureDuVersement(r);
    const moi = identite({ prenoms: r.prenoms, nom: r.nom });
    const professionnel = r.client === "professionnel";
    const numeroClient = siren(r.sirenClient);

    const blocs = [
        /* Émetteur à gauche, client à droite. Deux blocs `lignes` empilés
           disaient les mêmes choses sans dire laquelle était laquelle : sur un
           document qui part chez un client, c'est la première information à
           lire. */
        {
            type: "entete",
            colonnes: [
                {
                    libelle: "Émetteur",
                    lignes: [
                        `${moi}, entrepreneur individuel`,
                        ...lignesDAdresse(r.adresse),
                        `SIRET ${siret(r.siret).valeur}`,
                    ],
                },
                {
                    libelle: "Client",
                    lignes: [
                        nettoyer(r.nomClient),
                        ...lignesDAdresse(r.adresseClient),
                        ...(professionnel && !numeroClient.motif ? [`SIREN ${numeroClient.valeur}`] : []),
                    ],
                },
            ],
        },
        /* Trois faits d'une ligne chacun : ils se groupent plutôt que de
           s'espacer, sinon un devis de trois lignes de prestations occupait
           deux pages pour un tiers de contenu. */
        {
            type: "lignes",
            lignes: [
                `Devis établi le ${dateEnToutesLettres(r.dateDevis)}, valable jusqu'au ${dateEnToutesLettres(r.validiteJusquAu)} inclus.`,
                `Objet : ${nettoyer(r.objet)}`,
                `Lieu d'exécution : ${nettoyer(r.lieuExecution)}`,
            ],
        },
        {
            type: "tableau",
            entetes: ["Désignation", "Quantité", "Prix unitaire", "Montant"],
            largeurs: [78, 22, 30, 30],
            // Les trois colonnes de chiffres s'alignent à droite : c'est la
            // seule façon de comparer deux prix d'un coup d'oeil.
            alignements: ["gauche", "droite", "droite", "droite"],
            lignes: compte.lignes.map((l) => [
                l.designation,
                nombre(l.quantite),
                euros(l.prixUnitaire),
                euros(l.montant),
            ]),
            total: ["Total à payer", "", "", euros(compte.total)],
            // Le total est LE chiffre du devis : il se détache du reste.
            aplat: true,
        },
    ];

    blocs.push({
        type: "lignes",
        lignes: [
            tva.franchise
                ? tva.mention
                : `Montants hors taxes. TVA au taux de ${pourcent(tva.taux)} en sus.`,
            `Modalités de paiement : ${nettoyer(r.modalitesDePaiement)}`,
            r.devisPayant === "oui"
                ? `Devis payant : ${euros(montant(r.prixDuDevis).valeur)}`
                : "Devis établi à titre gratuit.",
        ],
    });

    if (versement.versement) {
        blocs.push({
            type: "paragraphe",
            texte:
                `Un acompte de ${eurosEnPhrase(compte.acompte)}, soit ${pourcent(compte.acomptePourcent)} du montant ` +
                `total, est demandé à la commande. Le solde de ${eurosEnPhrase(compte.solde)} est exigible à ` +
                "l'achèvement. Cette somme est un acompte et non des arrhes : la commande engage fermement " +
                "les deux parties.",
        });
    }

    if (r.devisPayant === "oui") {
        blocs.push({
            type: "paragraphe",
            texte:
                `Le prix du présent devis, ${eurosEnPhrase(montant(r.prixDuDevis).valeur)}, est déduit de la ` +
                "facture si le devis est accepté.",
        });
    }

    /* Le pavé du client dit déjà COMMENT on accepte : cette phrase-là n'a donc
       à dire que ce qu'un devis engage, et ce qu'il faut pour en sortir. */
    blocs.push({
        type: "paragraphe",
        texte:
            "Le présent devis vaut offre de contrat et devient ferme dès sa signature par le client. " +
            "Toute prestation supplémentaire fera l'objet d'un avenant écrit, daté et signé avant son exécution.",
    });

    /* LES DEUX PARTIES SE FONT FACE, comme sur tout devis imprimé. Le pavé du
       client reste VIDE : le paraphe tracé dans le parcours est celui de
       l'émetteur, et le poser là reviendrait à lui remettre un devis déjà signé
       en son nom. Le cadre, lui, dit où porter la mention : sans trait,
       personne ne sait où signer. */
    blocs.push({
        type: "signatures",
        colonnes: [
            {
                libelle: `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10))}`,
                nom: moi,
            },
            {
                paraphe: false,
                libelle: "Date, mention « Bon pour accord » et signature du client",
                nom: nettoyer(r.nomClient),
            },
        ],
    });

    return { titre: `Devis n° ${nettoyer(r.numero)}`, blocs };
}

/** Ce que le parcours annonce avant le téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const compte = totaux(reponses);
    if (!compte || !composerDevis(reponses, aujourdhui)) return null;
    const r = reponses;
    const tva = regimeDeTva(r);

    return [
        ["Devis", `n° ${nettoyer(r.numero)}, ${compte.lignes.length} ligne${compte.lignes.length > 1 ? "s" : ""}`],
        ["Total à payer", euros(compte.total)],
        ...(compte.acompte
            ? [
                  ["Acompte à la commande", `${euros(compte.acompte)}, soit ${pourcent(compte.acomptePourcent)}`],
                  ["Solde à l'achèvement", euros(compte.solde)],
              ]
            : []),
        ["TVA", tva.franchise ? tva.mention : `au taux de ${pourcent(tva.taux)}, en sus`],
        ["Validité de l'offre", `jusqu'au ${dateEnToutesLettres(r.validiteJusquAu)} inclus`],
    ];
}
